import React from "react";
import { Link } from "react-router-dom";
import {
  Printer,
  TrendingDown,
  ShieldCheck,
  Wrench,
  Workflow,
  CircleDollarSign,
  ArrowUpRight,
  CheckCircle2,
} from "lucide-react";
import { waLink } from "../lib/whatsapp";
import { useSiteSettings } from "../lib/siteSettings";
import PageHero from "../components/PageHero";

const SECTION_IMG =
  "https://static.prod-images.emergentagent.com/jobs/1b35fc0a-0293-4ff8-ac55-1a277592b1f0/images/9251f1703331ea0d61a840d98f27f1c315e21c4c66733d00c669c777b262c0e1.png";

const BENEFITS = [
  {
    icon: TrendingDown,
    title: "Redução de custos",
    desc: "Custo previsível por página. Sem investimento em equipamentos, sem surpresas.",
  },
  {
    icon: ShieldCheck,
    title: "Tecnologia atualizada",
    desc: "Equipamentos novos, com substituição programada. Sua operação nunca para.",
  },
  {
    icon: Wrench,
    title: "Manutenção inclusa",
    desc: "Suporte técnico, troca de peças e suprimentos sob um único contrato.",
  },
  {
    icon: CircleDollarSign,
    title: "Sem CAPEX",
    desc: "Tire o investimento do balanço. Pague conforme usa, mensalmente.",
  },
  {
    icon: Workflow,
    title: "Gestão centralizada",
    desc: "Monitoramento de consumo, relatórios e otimização contínua do parque.",
  },
  {
    icon: Printer,
    title: "Frota completa",
    desc: "Térmicas, laser, jato de tinta e multifuncionais — tudo em um só fornecedor.",
  },
];

const STEPS = [
  {
    n: "01",
    title: "Diagnóstico",
    desc: "Mapeamos seu parque atual, volume de impressão e necessidades específicas.",
  },
  {
    n: "02",
    title: "Proposta sob medida",
    desc: "Dimensionamos equipamentos, suprimentos e SLA conforme sua operação.",
  },
  {
    n: "03",
    title: "Instalação & treinamento",
    desc: "Entregamos, configuramos e capacitamos sua equipe. Você só usa.",
  },
  {
    n: "04",
    title: "Gestão contínua",
    desc: "Acompanhamento, manutenção preventiva e suporte ágil quando precisar.",
  },
];

const INCLUDED = [
  "Locação dos equipamentos (térmicas, laser, jato de tinta)",
  "Toners, cilindros e ribbons sempre disponíveis",
  "Manutenção preventiva e corretiva",
  "Suporte técnico especializado",
  "Substituição de equipamentos defeituosos",
  "Relatórios mensais de consumo",
  "Atendimento por WhatsApp, telefone e e-mail",
];

export default function Outsourcing() {
  const { settings } = useSiteSettings();
  return (
    <main data-testid="outsourcing-page" className="bg-white">
      <PageHero
        overline="SERVIÇO"
        title="Outsourcing"
        subtitle="Gestão completa do seu parque de impressão. Equipamentos, suprimentos, manutenção e suporte — tudo sob um único contrato previsível."
        image={settings.cover_outsourcing}
      />

      {/* INTRO */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 grid md:grid-cols-12 gap-12 items-start">
          <div className="md:col-span-7">
            <div className="font-mono-sm text-xs tracking-[0.3em] uppercase text-[#E84E34] mb-4">
              // POR QUE OUTSOURCING
            </div>
            <h2 className="font-display text-3xl md:text-5xl font-black tracking-tight leading-[1.05]">
              Pare de comprar impressoras. Comece a usar impressão.
            </h2>
            <div className="mt-6 space-y-4 text-base text-[#374151] leading-relaxed">
              <p>
                Outsourcing de impressão é o serviço que transforma um custo imprevisível e cheio
                de dor de cabeça em <strong>um único contrato mensal previsível</strong>, com tudo incluído:
                equipamentos, suprimentos, manutenção e suporte.
              </p>
              <p>
                Sua empresa para de se preocupar com tonner acabando, máquina parada ou compra de
                novos equipamentos. A Avantech assume essa gestão e <strong>devolve para você foco no
                que importa: o seu negócio</strong>.
              </p>
            </div>
            <div className="mt-8">
              <a
                href={waLink("Olá Avantech! Quero conhecer o serviço de Outsourcing.")}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="outsourcing-cta-top"
                className="inline-flex items-center gap-3 bg-[#E84E34] text-white font-bold px-7 py-4 uppercase text-xs tracking-[0.2em] hover:bg-[#CF412A] transition-colors"
              >
                Falar com especialista <ArrowUpRight size={18} />
              </a>
            </div>
          </div>
          <div className="md:col-span-5">
            <img src={SECTION_IMG} alt="Outsourcing de impressão" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="py-20 md:py-28 bg-[#F9F9F9]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
          <div className="mb-14 max-w-3xl">
            <div className="font-mono-sm text-xs tracking-[0.3em] uppercase text-[#E84E34] mb-4">
              // BENEFÍCIOS
            </div>
            <h2 className="font-display text-3xl md:text-5xl font-black tracking-tight leading-[1.05]">
              O que você ganha com Outsourcing Avantech.
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#E5E7EB] border border-[#E5E7EB]">
            {BENEFITS.map((b, i) => (
              <div
                key={i}
                data-testid={`outsourcing-benefit-${i}`}
                className="bg-white p-8 hover:bg-[#0F0F10] hover:text-white transition-colors group"
              >
                <b.icon size={32} strokeWidth={1.5} className="text-[#E84E34]" />
                <h3 className="mt-5 font-display text-xl font-bold">{b.title}</h3>
                <p className="mt-2 text-sm text-[#6B7280] group-hover:text-white/70 leading-relaxed">
                  {b.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
          <div className="grid md:grid-cols-12 gap-8 mb-14">
            <div className="md:col-span-6">
              <div className="font-mono-sm text-xs tracking-[0.3em] uppercase text-[#E84E34] mb-4">
                // COMO FUNCIONA
              </div>
              <h2 className="font-display text-3xl md:text-5xl font-black tracking-tight leading-[1.05]">
                4 passos, do diagnóstico à gestão contínua.
              </h2>
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#E5E7EB] border border-[#E5E7EB]">
            {STEPS.map((s, i) => (
              <div key={i} className="bg-white p-8 relative">
                <div className="font-mono-sm text-xs tracking-[0.25em] uppercase text-[#E84E34]">
                  {s.n}
                </div>
                <h3 className="font-display text-2xl font-black mt-3">{s.title}</h3>
                <p className="mt-3 text-sm text-[#6B7280] leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INCLUDED */}
      <section className="py-20 md:py-28 bg-[#0F0F10] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-15">
          <img src={settings.cover_outsourcing} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-16 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="font-mono-sm text-xs tracking-[0.3em] uppercase text-[#E84E34] mb-4">
              // INCLUÍDO
            </div>
            <h2 className="font-display text-3xl md:text-5xl font-black tracking-tight leading-[1.05]">
              Tudo o que precisa, sem letrinhas miúdas.
            </h2>
            <p className="mt-6 text-white/70 leading-relaxed">
              Um único contrato. Um único responsável. Resultado garantido.
            </p>
          </div>
          <ul className="md:col-span-7 space-y-px bg-white/10 border border-white/10">
            {INCLUDED.map((item, i) => (
              <li key={i} className="bg-[#0F0F10] flex items-start gap-4 p-5">
                <CheckCircle2 size={22} className="text-[#E84E34] flex-shrink-0 mt-0.5" strokeWidth={1.8} />
                <span className="text-base">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 text-center">
          <div className="font-mono-sm text-xs tracking-[0.3em] uppercase text-[#E84E34] mb-4">
            // VAMOS COMEÇAR
          </div>
          <h2 className="font-display text-3xl md:text-5xl font-black tracking-tight leading-[1.05] max-w-3xl mx-auto">
            Quanto custa? Depende da sua operação.
          </h2>
          <p className="mt-6 text-base text-[#374151] max-w-2xl mx-auto leading-relaxed">
            Cada contrato é dimensionado conforme volume, tipo de impressão e SLA desejado.
            Solicite um diagnóstico gratuito e receba uma proposta personalizada em até 24h úteis.
          </p>
          <div className="mt-10 flex flex-wrap gap-4 justify-center">
            <a
              href={waLink("Olá Avantech! Quero um diagnóstico gratuito do meu parque de impressão.")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="outsourcing-cta-bottom"
              className="inline-flex items-center gap-3 bg-[#E84E34] text-white font-bold px-7 py-4 uppercase text-xs tracking-[0.2em] hover:bg-[#CF412A] transition-colors"
            >
              Solicitar diagnóstico gratuito <ArrowUpRight size={18} />
            </a>
            <Link
              to="/contato"
              className="inline-flex items-center gap-3 bg-transparent border-2 border-[#1A1A1A] text-[#1A1A1A] font-bold px-7 py-4 uppercase text-xs tracking-[0.2em] hover:bg-[#1A1A1A] hover:text-white transition-colors"
            >
              Falar com o time
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
